import { ChevronDown, ChevronUp } from '@carbon/react/icons';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { PlainLink } from 'components/link';
import { EMPTY_CELL } from 'utils/empty-cell';
import type { CRMAccountResponse, EntityResponse } from 'types/api-responses';
import { formatAddress } from '../utils';
import style from './link-crm-account-modal.module.scss';

interface EntityInfoTableProps {
  entity: EntityResponse;
  existingLinkedAccounts: CRMAccountResponse[];
  expandedAccounts: boolean;
  onToggleExpanded: () => void;
}

/**
 * The entity being linked and the CRM accounts already linked to it.
 * @param {EntityInfoTableProps} props - The table's data and handlers
 * @return {JSX.Element} The table
 */
export function EntityInfoTable({
  entity,
  existingLinkedAccounts,
  expandedAccounts,
  onToggleExpanded,
}: EntityInfoTableProps) {
  return (
    <div className={style.entityInfoSection}>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader></TableHeader>
            <TableHeader>Entity ID</TableHeader>
            <TableHeader>Insights Entity</TableHeader>
            <TableHeader>Linked CRM Account</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              {existingLinkedAccounts && existingLinkedAccounts.length > 1 ? (
                <Button
                  kind="ghost"
                  size="sm"
                  className={style.expander}
                  aria-label={
                    expandedAccounts ? 'Collapse accounts' : 'Expand accounts'
                  }
                  aria-expanded={expandedAccounts}
                  onClick={onToggleExpanded}
                  renderIcon={expandedAccounts ? ChevronUp : ChevronDown}
                />
              ) : (
                <Button
                  kind="ghost"
                  size="sm"
                  className={style.expander}
                  disabled
                  aria-label="No expansion available"
                  renderIcon={ChevronDown}
                />
              )}
            </TableCell>
            <TableCell>{entity.id}</TableCell>
            <TableCell>
              <div className={style.entityInfo}>
                <div className={style.entityName}>
                  <PlainLink
                    href={entity.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {entity.name}
                  </PlainLink>
                </div>
                <div className={style.entityAddress}>
                  <span className={style.label}>Address:</span>
                  <span className={style.value}>
                    {' '}
                    {formatAddress(entity.addresses)}
                  </span>
                </div>
                <div className={style.entityType}>
                  <span className={style.label}>Entity Type:</span>
                  <span className={style.value}>
                    {' '}
                    {entity.type || EMPTY_CELL}
                  </span>
                </div>
              </div>
            </TableCell>
            <TableCell>
              {existingLinkedAccounts && existingLinkedAccounts.length > 0 ? (
                existingLinkedAccounts.length === 1 ? (
                  <div className={style.accountItem}>
                    <div className={style.accountName}>
                      <PlainLink
                        href={existingLinkedAccounts[0].salesforceHref}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {existingLinkedAccounts[0].name}
                      </PlainLink>
                    </div>
                    <div className={style.accountType}>
                      <span className={style.label}>Type:</span>
                      <span className={style.value}>
                        {' '}
                        {existingLinkedAccounts[0].type || EMPTY_CELL}
                      </span>
                    </div>
                    <div className={style.accountAddress}>
                      <span className={style.label}>Address:</span>
                      <span className={style.value}>
                        {' '}
                        {existingLinkedAccounts[0].billingAddress || EMPTY_CELL}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className={style.value}>
                    {existingLinkedAccounts.length} CRM Accounts
                  </span>
                )
              ) : (
                <span className={style.value}>Not Linked</span>
              )}
            </TableCell>
          </TableRow>
          {expandedAccounts &&
            existingLinkedAccounts &&
            existingLinkedAccounts.length > 1 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <div className={style.subTableWrapper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader
                            className={style.subTableHeaderCell}
                          ></TableHeader>
                          <TableHeader className={style.subTableHeaderCell}>
                            CRM Account
                          </TableHeader>
                          <TableHeader className={style.subTableHeaderCell}>
                            Type
                          </TableHeader>
                          <TableHeader className={style.subTableHeaderCell}>
                            Address
                          </TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {existingLinkedAccounts.map((account) => (
                          <TableRow key={account.salesforceId}>
                            <TableCell></TableCell>
                            <TableCell>
                              <div className={style.accountName}>
                                <PlainLink
                                  href={account.salesforceHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {account.name}
                                </PlainLink>
                              </div>
                            </TableCell>
                            <TableCell>{account.type || EMPTY_CELL}</TableCell>
                            <TableCell>
                              {account.billingAddress || EMPTY_CELL}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </Table>
    </div>
  );
}
